import { getPassBookings } from "../../../app/api/postCustomBookingsSearch";
import { isAnonymousSession } from "../../../Lib/Utils/SessionStorageUtil";
import { BaseAction } from "../../../core/actions/BaseAction";
import { getRecoilStateValue, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { IPassBookingSearchRequest, IUserResponse } from "../../../Orchestrator/Types/Index";
import dayjs from 'dayjs';
import { hideLoader, showLoader } from "../../../Lib/Utils/validationUtil";
import { getPassBookingObjectAction } from "./GetPassBookingObjectAction";
import { IPassBooking } from "../../../Orchestrator/Types/Types/BookingTypes";

export class GetBookedPassesAction extends BaseAction<IPassBooking[], void> {
  override async run(): Promise<IPassBooking[]> {
    var passBookings: IPassBooking[] = [];

    const userDataAtom = getRecoilStateValue<IUserResponse>("userDataAtom");

    if (!userDataAtom) { return [] }

    // Booked passes are account-scoped. userDataAtom alone is not a reliable
    // auth signal (it is persisted to localStorage and can rehydrate after the
    // Keycloak session expired) — check the live session, the same source of
    // truth ApiRequest uses to attach the Authorization header. Without this,
    // a disconnected visitor fires an unauthenticated bookings-search.
    if (await isAnonymousSession()) { return [] }

    showLoader();

    try {
      const currentDate = dayjs(new Date())
      const requestPayload: IPassBookingSearchRequest = {
        purchaseDateRange: {
          startTime: dayjs(currentDate).subtract(2, 'month').toISOString(),
          endDate: dayjs(currentDate).add(2, 'month').toISOString(),
        },
        purchaserPassengerEmail: userDataAtom.email,
        productCategories: ["PRODUCT_CATEGORY.TRAVEL_PASS"]
      }

      const allBookings = await getPassBookings(requestPayload, 30);
      if (allBookings.status === "ok" && allBookings.answer) {
        const bookingsWithPasses = allBookings.answer.bookingSearchResults.filter(
          (booking) =>
            booking.isTravelPass ||
            booking.passengers?.some((passenger) =>
              passenger.cards?.some((card) => card.type === "TRAVEL_PASS")
            )
        );

        const isTravelPassBookings: typeof bookingsWithPasses = [];
        const bookingsWithCards = new Map<string, typeof bookingsWithPasses[0]>();
        
        for (const booking of bookingsWithPasses) {
          if (booking.isTravelPass) {
            isTravelPassBookings.push(booking);
          } else {
            booking.passengers?.forEach((passenger) => {
              passenger.cards?.forEach((card) => {
                if (card.type === "TRAVEL_PASS" && card.number && card.issuer) {
                  const identifier = `${card.issuer}_${card.number}`;
                  const existingBooking = bookingsWithCards.get(identifier);
                  if (!existingBooking || new Date(booking.createdOn) > new Date(existingBooking.createdOn)) {
                    bookingsWithCards.set(identifier, booking);
                  }
                }
              });
            });
          }
        }

        const processedTravelAccounts = new Set<string>();
        const bookingsToProcess = [...isTravelPassBookings, ...bookingsWithCards.values()];
        
        for (const booking of bookingsToProcess) {
          getPassBookingObjectAction.setParams({ bookingId: booking.id });
          await getPassBookingObjectAction.execute();

          const result = getPassBookingObjectAction.result;
          if (result && result.travelAccount?.travelAccount) {
            const travelAccount = result.travelAccount.travelAccount;
            if (travelAccount.number && travelAccount.issuer) {
              const accountIdentifier = `${travelAccount.issuer}_${travelAccount.number}`;
              if (!processedTravelAccounts.has(accountIdentifier)) {
                processedTravelAccounts.add(accountIdentifier);
                passBookings.push(result);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("log Failed to get booked offers", error);
    } finally {
      updateRecoilState("passBookingsAtom", passBookings);
      hideLoader();
      return passBookings;
    }
  }
}

export const getBookedPassesAction = new GetBookedPassesAction();
